import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export default function InvestmentManagement({ user, onLogout }) {
  const [investments, setInvestments] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    investor_id: '',
    principal_amount: '',
    interest_rate: '',
    interest_type: 'simple',
    start_date: '',
    maturity_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [investmentsRes, investorsRes] = await Promise.all([
        axios.get(`${API}/admin/investments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/investors`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInvestments(investmentsRes.data);
      setInvestors(investorsRes.data.filter(inv => inv.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInvestment = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        principal_amount: parseFloat(formData.principal_amount),
        interest_rate: parseFloat(formData.interest_rate),
        start_date: new Date(formData.start_date).toISOString(),
        maturity_date: new Date(formData.maturity_date).toISOString()
      };
      await axios.post(`${API}/admin/investments`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Investment created successfully');
      setShowAddDialog(false);
      setFormData({
        investor_id: '',
        principal_amount: '',
        interest_rate: '',
        interest_type: 'simple',
        start_date: '',
        maturity_date: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create investment');
    }
  };

  const filteredInvestments = investments.filter(inv =>
    inv.investment_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.investor_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInvestorName = (investorId) => {
    const investor = investors.find(i => i.investor_id === investorId);
    return investor?.full_name || investorId;
  };

  return (
    <AdminLayout user={user} onLogout={onLogout} currentPage="investments">
      <div className="space-y-6" data-testid="investment-management-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Investments
            </h1>
            <p className="text-muted-foreground text-lg">Manage investment portfolios</p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <button data-testid="add-investment-button" className="btn-primary flex items-center space-x-2">
                <Plus className="w-4 h-4" />
                <span>Create Investment</span>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create New Investment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddInvestment} className="space-y-4" data-testid="add-investment-form">
                <div>
                  <label className="block text-sm font-medium mb-2">Investor *</label>
                  <select
                    data-testid="investment-investor-select"
                    required
                    value={formData.investor_id}
                    onChange={(e) => setFormData({...formData, investor_id: e.target.value})}
                    className="input-field"
                  >
                    <option value="">Select Investor</option>
                    {investors.map(inv => (
                      <option key={inv.investor_id} value={inv.investor_id}>
                        {inv.full_name} ({inv.investor_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Principal Amount (₹) *</label>
                    <input
                      data-testid="investment-amount-input"
                      type="number"
                      required
                      step="0.01"
                      min="0"
                      value={formData.principal_amount}
                      onChange={(e) => setFormData({...formData, principal_amount: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Interest Rate (%) *</label>
                    <input
                      data-testid="investment-rate-input"
                      type="number"
                      required
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.interest_rate}
                      onChange={(e) => setFormData({...formData, interest_rate: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Interest Type *</label>
                    <select
                      data-testid="investment-type-select"
                      required
                      value={formData.interest_type}
                      onChange={(e) => setFormData({...formData, interest_type: e.target.value})}
                      className="input-field"
                    >
                      <option value="simple">Simple Interest</option>
                      <option value="compound">Compound Interest</option>
                      <option value="monthly">Monthly Interest</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Start Date *</label>
                    <input
                      data-testid="investment-start-date-input"
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="input-field"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Maturity Date *</label>
                    <input
                      data-testid="investment-maturity-date-input"
                      type="date"
                      required
                      value={formData.maturity_date}
                      onChange={(e) => setFormData({...formData, maturity_date: e.target.value})}
                      className="input-field"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddDialog(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button data-testid="submit-investment-button" type="submit" className="btn-primary">
                    Create Investment
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="card">
          <div className="p-6 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                data-testid="search-investments-input"
                type="text"
                placeholder="Search investments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="investments-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Investment ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Investor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Principal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Current Value
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Interest Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                      Loading...
                    </td>
                  </tr>
                ) : filteredInvestments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">
                      No investments found
                    </td>
                  </tr>
                ) : (
                  filteredInvestments.map((investment) => (
                    <tr key={investment.investment_id} data-testid={`investment-row-${investment.investment_id}`} className="hover:bg-muted/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-sm">{investment.investment_id}</td>
                      <td className="px-6 py-4 text-sm">{getInvestorName(investment.investor_id)}</td>
                      <td className="px-6 py-4 font-mono font-semibold">₹{investment.principal_amount.toLocaleString('en-IN')}</td>
                      <td className="px-6 py-4 font-mono font-semibold text-success">₹{investment.current_value.toLocaleString('en-IN')}</td>
                      <td className="px-6 py-4 capitalize">{investment.interest_type}</td>
                      <td className="px-6 py-4 font-mono">{investment.interest_rate}%</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          investment.status === 'active' ? 'bg-success/10 text-success' :
                          investment.status === 'matured' ? 'bg-accent/10 text-accent' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {investment.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}